import React, { useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "../lib/api";

// Sold-out capture: shopper leaves an email, backend auto-notifies when the product is restocked.
// mode="popover" renders a small button that expands (for cards inside links);
// mode="inline" always shows the form (for the product detail page).
export const RestockNotify = ({ productId, productName, mode = "inline" }) => {
  const [open, setOpen] = useState(mode === "inline");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | error

  const guard = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Stop bubbling (so a parent <Link> doesn't navigate) WITHOUT preventDefault,
  // which would suppress the submit button's implicit form submission.
  const guardBubble = (e) => e.stopPropagation();

  const submit = async (e) => {
    guard(e);
    if (!email.trim() || state === "busy") return;
    setState("busy");
    try {
      await api.post("/restock-alerts", {
        product_id: productId,
        email: email.trim(),
        product_url: `${window.location.origin}/product/${productId}`,
      });
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <span
        className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-green-400 inline-flex items-center gap-1.5"
        data-testid={`restock-success-${productId}`}
      >
        <Check className="w-3.5 h-3.5" /> You&rsquo;re on the list
      </span>
    );
  }

  if (!open) {
    return (
      <button
        onClick={(e) => {
          guard(e);
          setOpen(true);
        }}
        className="font-mono-plex text-[10px] tracking-[0.15em] uppercase font-bold border border-zinc-600 text-zinc-300 px-3.5 py-2 inline-flex items-center gap-1.5 hover:border-orange-500 hover:text-orange-400 active:scale-95 transition-[border-color,color,transform] duration-200 focus:ring-2 focus:ring-orange-500 focus:outline-none"
        data-testid={`notify-me-${productId}`}
      >
        <Bell className="w-3 h-3" /> Notify Me
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      onClick={guardBubble}
      className={`flex items-center gap-2 ${mode === "popover" ? "absolute bottom-full right-0 mb-2 z-30 w-64 bg-[#0C1117] border border-zinc-700 shadow-2xl p-2" : "w-full max-w-md"}`}
      data-testid={`restock-form-${productId}`}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onClick={guardBubble}
        placeholder="Email for restock alert"
        className="flex-1 min-w-0 bg-[#04070A] border border-zinc-700 px-3 py-2 font-mono-plex text-xs text-zinc-200 placeholder:text-zinc-600 focus:ring-2 focus:ring-orange-500 focus:outline-none"
        data-testid={`restock-email-${productId}`}
      />
      <button
        type="submit"
        disabled={state === "busy"}
        className="shrink-0 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.15em] uppercase font-bold px-3 py-2 hover:bg-orange-600 active:scale-95 transition-[background-color,transform] duration-200 disabled:opacity-50 focus:ring-2 focus:ring-orange-500 focus:outline-none"
        data-testid={`restock-submit-${productId}`}
      >
        {state === "busy" ? "…" : "Alert Me"}
      </button>
      {state === "error" && (
        <span className="font-mono-plex text-[10px] text-red-400" data-testid={`restock-error-${productId}`}>Try again</span>
      )}
    </form>
  );
};
